<script>
    // main dressing export that includes the api
    import dressing from "@intellectability/platform-common";

    // common constants
    import {ACTIONS, STATES} from "@intellectability/platform-common/constants";

    // common Svelte components
    import {
        AutoUpdateInput,
        Button,
        Icon,
        PhoneNumberForm,
        Placeholder,
        toast,
        userSecurity
    } from "@intellectability/platform-common/sv";

    import {actionCheck, create_security_error} from "@intellectability/platform-common/utils";

    // bootstrap
    import {Breadcrumb, BreadcrumbItem, Col, Row, Table} from "@sveltestrap/sveltestrap";

    import {createForm} from "svelte-forms-lib";

    import * as yup from "yup";

    import {fade} from "svelte/transition";

    import ProviderRelationshipModal from "./lib/ProviderRelationshipModal.svelte";
    import ServiceUpdateTooltip from "./lib/ServiceUpdateTooltip.svelte";

    // this is here to prevent any user
    // from ever changing the IntellectAbility status
    const INTELLECTABILITY_ID = 1;

    /* @type {number} providerID */
    let {providerID} = $props();

    const {
        PROVIDER_DETAILS_CREATE,
        PROVIDER_DETAILS_VIEW,
        PROVIDER_DETAILS_UPDATE
    } = ACTIONS;

    let fetchingInitialData = $state(true);
    let fetchingProviderDetails = $state(false);

    let hasAccess = actionCheck(userSecurity.userActions);
    let isLoggedIn = $derived(userSecurity.loggedIn);

    let loading = $derived(fetchingInitialData || fetchingProviderDetails);
    let styleLoading = $state(true);
    let modalOpen = $state(false);

    let isExistingProvider = $derived(Number.isInteger(providerID));

    /**
     * @typedef {object} ProviderService
     * @property {number} svc_key_num = the id of the individual service
     * @property {string} svc_name = the name of the individual service
     *
     * @example
     * ```JavaScript
     * {
     *     "svc_key_num": 11,
     *     "svc_name": "Behavior Support Evaluation"
     * }
     * ```
     */

    /**
     * @typedef {object} ProviderDetails
     * @property {string} city
     * @property {string} county
     * @property {string} email
     * @property {string} id
     * @property {string} name
     * @property {string} other_id
     * @property {string} parent_id
     * @property {ProviderService[]} services
     * @property {string} state
     * @property {string} status - defaults to "5", which is Active
     * @property {string} street
     * @property {string} type
     * @property {string} web_url
     * @property {string} zip
     *
     * @see {@link ProviderService}
     */

    /** @type {ProviderDetails} */
    let providerDetails = $state({
        city: "",
        county: "",
        email: "",
        taco: "",
        id: "",
        name: "",
        other_id: "",
        parent_id: "",
        services: [],
        state: "",
        status: "5",
        street: "",
        type: "",
        web_url: "",
        zip: ""
    });

    /**
     * @typedef {Object} InitialValues - The object used to set up the provider form
     * @property {string} [city] - The city of the provider
     * @property {string} [county] - The county of the provider
     * @property {string} [email] - The email address of the provider
     * @property {number} [id] - The ID of the provider
     * @property {string} [name] - The name of the provider
     * @property {string} [other_id] - The other ID of the provider
     * @property {number} [parent_id] - The ID of the parent provider
     * @property {number} [status] - The status of the provider
     * @property {string} [street] - The street address of the provider
     * @property {string} [type] - The type of the provider
     * @property {string} [web_url] - The website URL of the provider
     * @property {string} [zip] - The zip code of the provider
     */

    /**
     * @type {InitialValues}
     */
    let initialValues = $derived.by(function () {
        return {
            city: providerDetails.city,
            county: providerDetails.county,
            email: providerDetails.email,
            id: providerDetails.id,
            name: providerDetails.name,
            other_id: providerDetails.other_id,
            parent_id: providerDetails.parent_id,
            status: providerDetails.status,
            street: providerDetails.street,
            state: providerDetails.state,
            type: providerDetails.type,
            web_url: providerDetails.web_url,
            zip: providerDetails.zip
        };
    });

    // top level store for provider phone numbers, needed for record creation
    let providerPhoneNumbers = $state.raw([]);

    let providerSelectedServices = $state([]);

    let showServiceUpdateResults = $state([]);

    let providerHasFullAddress = $derived.by(function () {
        let parts = ["street", "city", "state", "zip"];

        return parts.every(function (part) {
            return check(providerDetails[part]);
        });

        function check(str) {
            return typeof str === "string" && str.length > 0;
        }
    });

    let pageTitle = $derived(providerID !== undefined ? "Provider Details" : "Add New Provider");

    let field_margin_classes = "mb-4 mb-lg-4";

    const applicable_business_rule_names = {
        // if the person doesn't normally have edit permissions
        // and this rule is set, then allow them to ONLY change the provider status
        // otherwise fall back to the default permissions (edit or view)
        ALLOW_STATUS_CHANGE: "allow_provider_status_change"
    };

    // for dropdowns
    let providerTypes = $state.raw([]);
    let providerStatuses = $state([]);
    let providerCounties = $state.raw([]);
    let providerServices = $state.raw([]);
    let providerPossibleParents = $state.raw([]);
    let providerValues = $state({
        statuses: []
    });

    // used to list the users for this provider who have the role of provider admin
    // tied to the "provider_details_update_features" business rule which is checked on the server, not here
    let providerAdmins = $state([]);

    let formDisabledBySecurity = $derived.by(function () {

        if (isExistingProvider) {
            return !hasAccess(PROVIDER_DETAILS_UPDATE);
        }

        return !hasAccess(PROVIDER_DETAILS_CREATE);
    });

    // this rule will drive whether or not the logged in user
    // can update certain fields of the provider record
    // (address, email, website, phone)
    let provider_admin_rule = "provider_details_update_features";

    // so-called "provider admin" (not to be confused with "provider administrator") roles
    // are granted authority to edit specific fields
    let isProviderAdmin = $derived.by(function () {
        return userSecurity.businessRules.find(e => e.rule_name === provider_admin_rule) !== undefined;
    });

    let commonInputProps = $derived({
        autoupdate: isExistingProvider,
        entityID: providerID,
        loading: loading,
        route: "providers/update"
    });

    // update providers = action
    // update status = business rule
    // update specific fields = business rule

    const form_props = {
        initialValues,
        validationSchema: yup.object().shape({
            city: yup.string(),
            county: yup.string(),
            email: yup.string().email(),
            name: yup.string().required("Name is required"),
            other_id: yup.string(),
            parent_id: yup.string(),
            state: yup.string(),
            status: yup.number().required("Status is required"),
            street: yup.string(),
            type: yup.string().required("Type is required"),
            web_url: yup.string().url(),
            zip: yup.string()
        }),
        onSubmit
    };

    const {form, errors, handleSubmit, updateInitialValues} = createForm({
        initialValues: form_props.initialValues,
        validationSchema: form_props.validationSchema,
        onSubmit: form_props.onSubmit
    });

    $effect(function reInitForm() {
        // allows us to update the initial form values when changed via an
        // api call (update or load)
        updateInitialValues(initialValues);
    });

    $effect(function load() {
        if (isLoggedIn) {

            if (Number.isInteger(parseInt(providerID, 10))) {
                if (!hasAccess(PROVIDER_DETAILS_VIEW)) {

                    const security_error = create_security_error("App.svelte",
                        "ProviderForm.svelte", PROVIDER_DETAILS_VIEW);

                    dressing.api.report_security_error(security_error)
                        .then(function () {
                            // regardless of response, log out
                            dressing.api.logout();
                        });
                    return;
                }
            }

            const needed_initial_data = [
                dressing.get("/providers/types").then(({data: res}) => {
                    if (res.success === true) {
                        const {types} = res.data;
                        if (Array.isArray(types) && types.length > 0) {
                            providerTypes = types;
                        }
                    }
                    return true;
                }),
                dressing.get("/providers/statuses").then(({data: res}) => {
                    if (res.success === true) {
                        const {statuses} = res.data;
                        if (Array.isArray(statuses) && statuses.length > 0) {
                            // convert to an options-ready array
                            providerStatuses = statuses.map(function (status) {
                                return {
                                    value: status.id,
                                    label: status.status
                                };
                            });
                            providerValues.statuses = providerStatuses;
                        }
                    }
                    return true;
                }),
                dressing.get("/providers/services").then(({data: res}) => {
                    if (res.success === true) {
                        const {services} = res.data;
                        if (Array.isArray(services) && services.length > 0) {
                            providerServices = services;
                        }
                    }
                    return true;
                })
            ];

            // If we don't have a a provider ID and we are allowing user to set a parent provider,
            // fetch all providers by using 0 as the "provider ID"

            if (!providerID) {
                needed_initial_data.push(dressing.get((`/providers/possible_parents/0`))
                    .then(({data: res}) => {
                        const {success, data} = res;
                        if (success === true) {
                            const providers = data;
                            if (Array.isArray(providers) && providers.length > 0) {
                                providerPossibleParents = providers;
                                return true;
                            }
                        }
                        return false;
                    })
                );
            }

            if (providerID) {
                fetch_provider_details();
            }

            Promise.allSettled(needed_initial_data)
                .then(function (resolved) {

                    const result = resolved.every(r => r.value === true);

                    if (result) {
                        return fetchingInitialData = false;
                    }
                    else {
                        toast.error("Error loading initial data");
                    }
                });
        }
    });

    function onSubmit(values) {

        const numbers = Object.keys(providerPhoneNumbers).map(function (key) {
            return {type: key, value: providerPhoneNumbers[key]};
        });

        // add providerPhoneNumbers to the body
        const body = {
            phone_numbers: numbers,
            ...values
        };

        dressing.post("/providers/create", {
            ...body
        }).then(({data: res}) => {
            if (res.success === true) {
                const {new_id} = res.data;
                if (new_id) {
                    providerID = new_id;
                    fetch_provider_details();
                }
                toast.success("Provider record created successfully");
            }
            return true;
        }).catch(function () {
            toast.error("Couldn't create provider record. Please check the form or contact support.");
        });
    }

    function fetch_provider_details() {

        if (isLoggedIn && providerID) {
            fetchingProviderDetails = true;
            return dressing.get(`/providers/${providerID}`)
                .then(({data: res}) => {

                    const {success, data} = res;

                    if (success === true) {
                        const {provider} = data;

                        providerDetails = {
                            id: provider.id ?? "",
                            name: provider.name ?? "",
                            type: provider.type ?? "",
                            other_id: provider.other_id ?? "",
                            status: (provider.status ?? "") + "",
                            street: provider.street ?? "",
                            city: provider.city ?? "",
                            state: provider.state ?? "",
                            zip: provider.zip ?? "",
                            county: provider.county ?? "",
                            email: provider.email ?? "",
                            web_url: provider.web_url ?? "",
                            parent_id: provider.parent_id ?? ""
                        };

                        if (Array.isArray(provider.services) && provider.services.length > 0) {
                            providerSelectedServices = provider.services;
                        }

                        if (Array.isArray(provider.provider_admins) && provider.provider_admins.length > 0) {
                            providerAdmins = provider.provider_admins;
                        }

                        if (typeof provider.state === "string" && provider.state.length === 2) {
                            dressing.get("/application/counties/" + provider.state)
                                .then((res) => {
                                    if (res.success === true && Array.isArray(res.data.counties)) {
                                        providerCounties = res.data.counties;
                                    }
                                });
                        }

                        dressing.get((`/providers/possible_parents/${providerID}`))
                            .then(({data: res}) => {
                                const {success, data} = res;
                                if (success === true) {
                                    const providers = data;
                                    if (Array.isArray(providers) && providers.length > 0) {
                                        providerPossibleParents = providers;
                                    }
                                }
                            });
                    }

                    fetchingProviderDetails = false;
                });
        }
    }

    function is_status_disabled() {

        // do not disable the status input if the user has this business rule
        if (providerID) {
            const business_rule_applies = userSecurity.businessRules.find(
                e => e.rule_name === applicable_business_rule_names.ALLOW_STATUS_CHANGE);
            return business_rule_applies ? !business_rule_applies : formDisabledBySecurity;
        }

        // fallback to initial security setting
        return formDisabledBySecurity;
    }

    function get_gmaps_address(providerObject) {

        const parts = ["street", "city", "state", "zip"];
        const p = providerObject;

        const full_address = parts
            .map(part => (p[part] || ""))
            .join(" ")
            .trim();

        return full_address.replaceAll(" ", "+");

    }

    function on_update({field, value}) {
        providerDetails[field] = value;
    }

    function toggle_modal() {
        modalOpen = !modalOpen;
    }

</script>

<!-- Breadcrumbs -->
<Breadcrumb divider={" "} class="breadcrumbs mb-3">
    <BreadcrumbItem>
        <a href="/index.php?action=30000">
            <Icon icon="briefcase" class="me-1"/>
            providers
        </a>
    </BreadcrumbItem>
    <BreadcrumbItem class="mx-3">
        <Icon icon="chevron-right"/>
    </BreadcrumbItem>
    <BreadcrumbItem>
        {providerID ? "provider details for " + providerDetails.name : "add new provider"}
    </BreadcrumbItem>
</Breadcrumb>
<!-- Page Title -->
<h1 class="text-primary-700 mb-4">
    {#if loading}
        <Placeholder/>
    {:else}
        {#if isExistingProvider}
            {pageTitle + " for "}<span class="text-primary-500">{providerDetails.name}</span>
        {:else}
            <Icon icon="briefcase" class="mr-1"/>
            Add New Provider
        {/if}
    {/if}
</h1>
<form onsubmit={handleSubmit}>
    {#key providerDetails}
        <fieldset disabled={loading}>
            <Row>
                <Col xs="12" lg="4" class={field_margin_classes}>
                    <AutoUpdateInput
                            bind:defaultValue={$form.name}
                            disabled={formDisabledBySecurity}
                            fieldName="name"
                            label="Provider Name"
                            invalid={$errors.name.length > 0}
                            invalidMessage="Please enter a provider name"
                            onUpdate={on_update}
                            {...commonInputProps}
                    />
                </Col>
                <Col xs="12" lg="4" class={field_margin_classes}>
                    <AutoUpdateInput
                            bind:defaultValue={$form.type}
                            disabled={formDisabledBySecurity}
                            fieldName="type"
                            invalid={$errors.type.length > 0}
                            invalidMessage="Please select a provider type"
                            label="Provider Type"
                            onUpdate={on_update}
                            optionsFrom={providerTypes}
                            type="select"
                            {...commonInputProps}
                    />
                </Col>
                <Col xs="12" lg="4" class={field_margin_classes}>
                    <AutoUpdateInput
                            bind:defaultValue={$form.other_id}
                            disabled={formDisabledBySecurity}
                            entityID={providerID}
                            fieldName="other_id"
                            label="Other ID"
                            onUpdate={on_update}
                            {...commonInputProps}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs="12" lg="4" class={field_margin_classes}>
                    <AutoUpdateInput
                            bind:defaultValue={$form.status}
                            disabled={providerID === INTELLECTABILITY_ID || is_status_disabled()}
                            entityID={providerID}
                            fieldName="status"
                            invalid={$errors.status.length > 0}
                            invalidMessage="Please select a provider status"
                            label="Provider Status"
                            onUpdate={on_update}
                            optionsFrom={providerStatuses}
                            type="select"
                            {...commonInputProps}
                    />
                </Col>
                <Col xs="12" lg="8" class={field_margin_classes}>
                    <Row>
                        <Col>
                            {#key providerPossibleParents}
                                <AutoUpdateInput
                                        bind:defaultValue={$form.parent_id}
                                        disabled={formDisabledBySecurity}
                                        fieldName="parent_id"
                                        type="select"
                                        onUpdate={on_update}
                                        optionsFrom={providerPossibleParents}
                                        label={"Parent Provider"}
                                        {...commonInputProps}
                                />
                            {/key}
                        </Col>
                        {#if $form.parent_id !== "" && providerID}
                            <Col xs="auto" class="align-content-center ps-0">
                                <Button pill onclick={toggle_modal}>
                                    More info
                                </Button>
                            </Col>
                        {/if}
                    </Row>
                </Col>
            </Row>
            <Row class="">
                <Col md={6} xs={12} class="">
                    <h2 class="mb-3">Provider Address</h2>
                    <Row class="gx-2">
                        <Col xs={12} lg={6} class={field_margin_classes}>
                            <AutoUpdateInput
                                    bind:defaultValue={$form.street}
                                    disabled={formDisabledBySecurity && !isProviderAdmin}
                                    fieldName="street"
                                    label="Address"
                                    loading={loading}
                                    onUpdate={on_update}
                                    type="text"
                                    {...commonInputProps}
                            />
                        </Col>
                        <Col xs={12} lg={6} class={field_margin_classes}>
                            <AutoUpdateInput
                                    bind:defaultValue={$form.city}
                                    disabled={formDisabledBySecurity && !isProviderAdmin}
                                    fieldName="city"
                                    label="City"
                                    onUpdate={on_update}
                                    type="text"
                                    {...commonInputProps}
                            />
                        </Col>
                    </Row>
                    <Row class="gx-2">
                        <Col xs={12} lg={6} class={field_margin_classes}>
                            <AutoUpdateInput
                                    bind:defaultValue={$form.state}
                                    disabled={formDisabledBySecurity && !isProviderAdmin}
                                    fieldName="state"
                                    label="State"
                                    onUpdate={on_update}
                                    optionsFrom={STATES}
                                    type="select"
                                    {...commonInputProps}
                            />
                        </Col>
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    bind:defaultValue={$form.zip}
                                    fieldName="zip"
                                    disabled={formDisabledBySecurity && !isProviderAdmin}
                                    label="Zip"
                                    onUpdate={on_update}
                                    type="text"
                                    {...commonInputProps}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col md={6} xs={12}>
                    <h2 class="mb-3">Contact Information</h2>
                    <Row>
                        <Col xs={12} lg={6} class={field_margin_classes}>
                            <AutoUpdateInput
                                    bind:defaultValue={$form.email}
                                    disabled={formDisabledBySecurity && !isProviderAdmin}
                                    fieldName="email"
                                    label="Provider Email"
                                    onUpdate={on_update}
                                    type="email"
                                    {...commonInputProps}
                            />
                        </Col>
                        <Col xs={12} lg={6} class={field_margin_classes}>
                            <AutoUpdateInput
                                    bind:defaultValue={$form.web_url}
                                    disabled={formDisabledBySecurity && !isProviderAdmin}
                                    fieldName="web_url"
                                    label="Provider Website"
                                    onUpdate={on_update}
                                    type="url"
                                    {...commonInputProps}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row class="mb-3">
                <Col xs={12} lg={6} class="col-12 col-lg-6">
                    <PhoneNumberForm
                            disabled={formDisabledBySecurity && !isProviderAdmin}
                            entityID={providerID}
                            entityType="provider"
                            onUpdate={function(values) {
                                            providerPhoneNumbers = values;
                                        }}
                    />
                </Col>

                {#if providerHasFullAddress}
                    {@const address = get_gmaps_address(providerDetails)}
                    <Col xs={12} lg={6} class="col-12 col-lg-6">
                        <h2 class="">Location</h2>
                        <iframe title="Google Maps Provider Location" height="100%" width="100%"
                                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyADk8Pc-KDTHLxiqcSR3SocPSSwvAI91ec&q=${address}`}></iframe>
                    </Col>
                {/if}
            </Row>
            {#if !isExistingProvider && !formDisabledBySecurity}
                <Button color="primary" class="rounded-pill" type="submit">submit</Button>
            {/if}
        </fieldset>
    {/key}
</form>

{#if providerAdmins.length > 0}
    <div transition:fade>
        <div class="d-flex align-items-center mb-2 justify-content-between">
            <div>
                <h4 class="mb-0">Provider Admins</h4>
            </div>
            <wa-icon class="help-icon" id="parent-help" name="circle-question" variant="solid"></wa-icon>
            <wa-tooltip for="parent-help" placement="top-end" unsafe>
                <div class="mb2">
                    This lists users assigned to this employer with the
                    <kbd class="text-warning-400">Provider Admin</kbd>
                    role. These users have limited permissions to edit their employer record.
                </div>
                <div>
                    Provider Admins may edit
                </div>
                <ul>
                    <li>Address</li>
                    <li>Email</li>
                    <li>Website</li>
                    <li>Phone</li>
                </ul>
            </wa-tooltip>
        </div>
        {#each providerAdmins as admin, i}
            {@const [name, username] = admin.split(" (")}
            <ol class="list-group ">
                <li class="list-group-item d-flex justify-content-between align-items-start mb-2">
                    <div class="ms-2 me-auto">
                        <div class="fw-bold mb-1">{i + 1}. Name <span
                                class="ms-2 text-secondary-900">(Username)</span>
                        </div>
                        <div class="ms-3">{name}<span class="ms-2 text-secondary-900">({username})</span>
                        </div>
                    </div>
                </li>
            </ol>
        {/each}
    </div>
{/if}

{#if providerID}
    <hr class="my-4">
    <div class="alert alert-primary">
        The following section should save as changes are made; there is no submit button needed.
    </div>
    <h2>Services Provided</h2>
    <fieldset disabled={formDisabledBySecurity}>
        <Table hover striped>
            <thead>
            <tr>
                <th>Service</th>
                <th>Provided</th>
            </tr>
            </thead>
            <tbody>

            {#each providerServices.sort(function (a, b) {
                return a.svc_key_num - b.svc_key_num;
            }) as service (service.svc_key_num)}
                {@const selected = (function () {
                    let id = service.svc_key_num;
                    return providerSelectedServices.some(function (selected_service) {
                        return selected_service.svc_key_num === id;
                    });
                })()}
                <tr class="pointer" onclick={function () {
                        }}>
                    <td>
                        <span class="text-secondary-500 me-2">{service.svc_key_num}.</span>
                        {service.svc_name}
                    </td>
                    <td style="width: 1%; white-space: nowrap">
                        <wa-switch id={"service-" + service.svc_key_num}
                                   defaultChecked={selected}
                                   checked={selected}
                                   onchange={function (e) {

                                           const method = e.target.checked ? "post" : "delete";
                                           const add_delete = e.target.checked ? "" : "/delete";

                                           const svc_key_num = service.svc_key_num;

                                           const payload = {
                                                provider_id: providerID,
                                                service_id: svc_key_num
                                           }

                                           // delete method requires the payload wrapped in a "data" key
                                           const body = method === "delete" ? {
                                               data: payload
                                           } : payload;

                                           dressing[method]("providers/services" + add_delete, body)
                                           .then(function ({data: res}) {
                                                const {success} = res;
                                                if (success === true) {
                                                    if (e.target.checked) {
                                                        providerSelectedServices.push(service);
                                                    }
                                                    else {
                                                        providerSelectedServices = providerSelectedServices.filter(
                                                            function (selected_service) {
                                                                return selected_service.svc_key_num !== svc_key_num;
                                                            })
                                                    }
                                                    showServiceUpdateResults.push(svc_key_num);
                                                } else {
                                                    e.target.checked = !e.target.checked;
                                                    toast.error("Could not update service")
                                                }
                                           })
                                       }} type="switch"></wa-switch>
                        <ServiceUpdateTooltip
                                show={showServiceUpdateResults.includes(service.svc_key_num)}
                                for={"service-" + service.svc_key_num}
                                isSelected={selected}
                                onhide={function (){
                                            showServiceUpdateResults = showServiceUpdateResults.filter(e => e !== service.svc_key_num);
                                        }}
                        />
                    </td>
                </tr>
            {/each}
            </tbody>
        </Table>
    </fieldset>
{/if}

<!-- Can't fetch a family relationship if the provider id doesn't exist -->
{#if providerID}
    <ProviderRelationshipModal
            {providerID}
            bind:modalOpen={modalOpen}
            onproviderselected={function (newProviderID){
                    modalOpen = false;
                    providerID = newProviderID;
                    fetch_provider_details();
                }}
    />
{/if}

<style>
    wa-icon.help-icon {
        display: inline-block;
        line-height: 0;
        height: 1.4rem;
        color: var(--hrst-error-500);
        font-size: 1.3rem;
        cursor: pointer;
    }
</style>